import AWS from "aws-sdk";
import { getAmplifyIdToken } from "./amplifyHelpers";
import store from "src/page/store";
import { AppConfig, getConfig } from "src/config/Config";
import { immediateToast } from "izitoast-react";
import { EMPTY_STRING } from "src/components/constant/constant";

export interface uploadFileToS3Prop {
  file: File;
  fileName: string;
  S3Bucket: string;
  MetaData?: {
    object_id?: string;
    object_type?: string;
    asset_type?: string;
    file_type?: string;
    date?: string;
  };
  contentType?: string;
}

const setupAWSCredentials = (token: string, config: AppConfig) => {
  const credentials = new AWS.CognitoIdentityCredentials({
    IdentityPoolId: config.IDENTITY_POOL_ID,
    Logins: {
      [`cognito-idp.${config.REGION}.amazonaws.com/${config.COGNITO_USER_POOL_ID}`]: token
    }
  });
  // CPC-1: drop any previously-cached (possibly unauthenticated) identity id so
  // the upload resolves a fresh AUTHENTICATED identity. Otherwise a stale
  // unauthenticated identity can be reused, whose role has no PutObject on the
  // asset bucket — receipt upload then 403s.
  credentials.clearCachedId();
  AWS.config.credentials = credentials;
};

const handleS3Upload = (
  s3: AWS.S3,
  params: AWS.S3.PutObjectRequest,
  fileName: string,
  resolve: (_value: AWS.S3.ManagedUpload.SendData) => void,
  reject: (_reason?: Error) => void
) => {
  s3.putObject(params)
    .on("httpUploadProgress", (evt) => {
      if (process.env.REACT_APP_BUILD_ENV !== "production") {
        console.log(`Upload Progress: ${Math.round((evt.loaded / evt.total) * 100)}%`);
      }
    })
    .send((err, data) => {
      if (err) {
        reject(err);
        // CPC-1: a 403 here means storage permissions (identity-pool role vs
        // asset bucket), not a problem with the file — say so explicitly.
        const forbidden = (err as { statusCode?: number })?.statusCode === 403;

        immediateToast("error", {
          message: forbidden
            ? `Upload of ${fileName} was refused by storage (permission denied). Please contact support.`
            : `Failed to upload ${fileName}`,
          timeout: forbidden ? 5000 : 3000,
          position: "topCenter"
        });
      } else {
        resolve(data as AWS.S3.ManagedUpload.SendData);
        immediateToast("success", {
          message: `${fileName} uploaded successfully`,
          timeout: 3000,
          position: "topCenter"
        });
      }
    });
};

export const uploadFileToS3 = async (uploadData: uploadFileToS3Prop): Promise<AWS.S3.ManagedUpload.SendData> => {
  const params = {
    ContentDisposition: "inline",
    Body: uploadData.file,
    Bucket: uploadData.S3Bucket,
    Key: uploadData.fileName,
    Metadata: uploadData.MetaData,
    ...(uploadData?.contentType && { ContentType: uploadData.contentType })
  };

  const config = getConfig();
  const { auth } = store.getState();

  // CPC-1: only ever configure AUTHENTICATED credentials (below). Previously this
  // pre-set unauthenticated credentials (empty Logins), which could leave the S3
  // request running as the unauthenticated identity — whose role has no access to
  // the asset bucket, so receipt upload/view then 403s.
  AWS.config.update({ region: config.REGION });

  const amplifyToken = await getAmplifyIdToken();

  return new Promise((resolve, reject) => {
    if (amplifyToken) {
      try {
        setupAWSCredentials(amplifyToken, config);

        const s3 = new AWS.S3({ params: { Bucket: uploadData.S3Bucket }, region: config.REGION });

        handleS3Upload(s3, params, uploadData.file.name, resolve, reject);
      } catch (e) {
        // Surface the real exception — this path fires on a client-side throw
        // BEFORE any network request (e.g. an aws-sdk env/polyfill error), which
        // otherwise hides behind the generic toast with nothing in the network tab.
        console.error("Receipt upload failed during client-side setup:", e);
        reject(new Error("Failed to set up AWS credentials for file upload"));
        immediateToast("error", {
          message: `Failed to upload ${uploadData.file.name}`,
          timeout: 3000,
          position: "topCenter"
        });
      }
    } else if (auth.cognitoIdToken) {
      try {
        setupAWSCredentials(auth.cognitoIdToken, config);

        const s3 = new AWS.S3({ params: { Bucket: uploadData.S3Bucket }, region: config.REGION });

        handleS3Upload(s3, params, uploadData.file.name, resolve, reject);
      } catch (e) {
        // Surface the real exception — this path fires on a client-side throw
        // BEFORE any network request (e.g. an aws-sdk env/polyfill error), which
        // otherwise hides behind the generic toast with nothing in the network tab.
        console.error("Receipt upload failed during client-side setup:", e);
        reject(new Error("Failed to set up AWS credentials for file upload"));
        immediateToast("error", {
          message: `Failed to upload ${uploadData.file.name}`,
          timeout: 3000,
          position: "topCenter"
        });
      }
    } else {
      reject(new Error("No valid authentication token found for file upload"));
    }
  });
};

const getS3SignedUrl = async (s3: AWS.S3, params: AWS.S3.GetObjectRequest): Promise<string> => {
  try {
    return await s3.getSignedUrlPromise("getObject", params);
  } catch (e) {
    console.error("Error getting signed URL:", e);
    return EMPTY_STRING;
  }
};

export const getSignedUrlFromS3 = async (
  bucketName: string,
  key: string,
  download: boolean = false
): Promise<string> => {
  if (!key) {
    throw new Error("Key parameter is required for S3 operations");
  }

  const params = {
    Bucket: bucketName,
    Key: key,
    ...(download && { ResponseContentDisposition: "attachment" })
  };

  const config = getConfig();
  const { auth } = store.getState();

  // CPC-1: only ever configure AUTHENTICATED credentials (below). Previously this
  // pre-set unauthenticated credentials (empty Logins), which could leave the S3
  // request running as the unauthenticated identity — whose role has no access to
  // the asset bucket, so receipt upload/view then 403s.
  AWS.config.update({ region: config.REGION });

  const amplifyToken = await getAmplifyIdToken();

  return new Promise<string>((resolve) => {
    if (amplifyToken) {
      (async () => {
        try {
          setupAWSCredentials(amplifyToken, config);

          const s3 = new AWS.S3({ params: { Bucket: bucketName }, region: config.REGION });
          const signedUrl = await getS3SignedUrl(s3, params);

          resolve(signedUrl);
        } catch (e) {
          console.error("Error setting up AWS credentials:", e);
          resolve(EMPTY_STRING);
        }
      })();
    } else if (auth.cognitoIdToken) {
      (async () => {
        try {
          setupAWSCredentials(auth.cognitoIdToken as string, config);

          const s3 = new AWS.S3({ params: { Bucket: bucketName }, region: config.REGION });
          const signedUrl = await getS3SignedUrl(s3, params);

          resolve(signedUrl);
        } catch (e) {
          console.error("Error setting up AWS credentials for SSO user:", e);
          resolve(EMPTY_STRING);
        }
      })();
    } else {
      resolve(EMPTY_STRING);
    }
  });
};

export const processFileName = (fileName: string, transactionId: string) => {
  const { auth } = store.getState();
  const currentDate = Math.floor(Date.now() / 1000);
  const organisationId = String(auth?.cognitoConfig?.currentOrganisation?.organisation_id);
  const lastDotIndex = fileName.lastIndexOf(".");
  const name = fileName.substring(0, lastDotIndex);
  const extension = fileName.substring(lastDotIndex + 1);

  const formattedName = name.replace(/[^a-zA-Z0-9]/g, "_").toLowerCase();

  const keyName = `${organisationId}/transaction/${transactionId}/${formattedName}_${currentDate}.${extension}`;

  return keyName;
};

export const isPdfFile = (fileName?: string): boolean => {
  return fileName?.toLowerCase().endsWith(".pdf") ?? false;
};
